import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Skeleton — loading placeholder that mirrors the shape of the content it
 * stands in for (the system default over spinners for content loading).
 * Purely decorative: it is aria-hidden and the loading container should set
 * `aria-busy="true"` plus a status message. Shape it with size/rounded
 * utilities so the loading layout matches the loaded layout (no shift).
 */
function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "animate-pulse rounded-sm bg-bg-300 motion-reduce:animate-none",
        className,
      )}
      {...props}
    />
  );
}

/**
 * SkeletonText — paragraph-shaped skeleton: `lines` bars with the last one
 * shortened, approximating rendered prose. Compose inside the same padding
 * the real text will occupy.
 */
function SkeletonText({
  lines = 3,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={cn("flex w-full flex-col gap-2", className)}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          className={cn("h-4", i === lines - 1 ? "w-2/3" : "w-full")}
        />
      ))}
    </div>
  );
}

export { Skeleton, SkeletonText };
